import Image from '@/components/Image';
import Link from '@/components/Link';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp, paddingX } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC } from 'react';
import styled from 'styled-components';

export interface WinnerCardProps {
  customerName: string;
  customerLogoUrl: string;
  cta?: {
    text: string;
    href: string;
  };
  tags?: Tag[];
}

interface Tag {
  text: string | null;
}

const CustomerAwardWinnerCard: FC<WinnerCardProps> = (props) => {
  const { tags } = props;

  return (
    <WinnerCard>
      <WinnerDetails>
        <Customer>
          <CustomerLogo>
            <Image src={props.customerLogoUrl} alt={props.customerName} height="80" width="80" />
          </CustomerLogo>

          <CustomerName>{props.customerName}</CustomerName>
        </Customer>

        {tags && <Tags>{tags?.map((tag, i) => tag.text && <Tag key={'tag-' + i}>{tag.text}</Tag>)}</Tags>}
      </WinnerDetails>

      {props.cta?.href && (
        <WinnerCTA>
          <Link href={props.cta.href}>
            <a>{props.cta?.text ?? 'View this win'} &rarr;</a>
          </Link>
        </WinnerCTA>
      )}
    </WinnerCard>
  );
};

export default CustomerAwardWinnerCard;

// #region - Components
const WinnerCard = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  box-shadow: 0px 0px 20px 0px ${hexToRGBA(COLORS.BLACK, 0.1)};
  display: grid;
  row-gap: ${remCalc(20)};
  width: 100%;

  ${mediaBreakpointUp('desktop')} {
    width: auto;
  }
`;

const WinnerDetails = styled.div`
  display: grid;
  row-gap: ${remCalc(20)};
  ${paddingX(20)};
  padding-top: ${remCalc(20)};
`;

const Customer = styled.div`
  display: flex;
  column-gap: ${remCalc(20)};
  align-items: center;
`;

const CustomerLogo = styled.div`
  img {
    border-radius: ${remCalc(10)};
  }
`;

const CustomerName = styled.div`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)};
  }
`;

const Tags = styled.div`
  min-height: ${remCalc(84)};
`;

const Tag = styled.div`
  ${customTextSize(16, 16)};
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(4)};
  padding: ${remCalc(8)};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  text-transform: uppercase;
  display: inline-block;
  margin: 0 ${remCalc(10)} ${remCalc(10)} 0;
`;

const WinnerCTA = styled.div`
  border-top: 1px solid ${COLORS.GRAY.HIGHLIGHT};
  padding: ${remCalc(20)};
  text-align: center;
  width: 100%;

  a {
    font-weight: ${FONT_WEIGHTS.BOLD};
  }
`;
// #endregion - Components
